import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Users, ArrowUpRight } from "lucide-react";
import SectionHead from "./SectionHead";
import { waLink } from "@/lib/site";

const FILTERS = [
  { id: "all", label: "All" },
  { id: "suv", label: "Premium SUVs" },
  { id: "sedan", label: "Sedans" },
  { id: "tempo", label: "Tempo Travellers" },
  { id: "hatch", label: "Hatchbacks" },
];

export default function Fleet({ vehicles }) {
  const [filter, setFilter] = useState("all");
  const visible = vehicles.filter((f) => filter === "all" || f.category === filter);

  return (
    <section id="fleet" data-testid="fleet-showcase-section" className="mx-auto max-w-7xl px-4 py-24 sm:px-8 lg:px-16">
      <SectionHead
        overline="The fleet"
        title="Cars that make the road feel short"
        sub="Transparent, indicative per-kilometre rates — your exact quote arrives on WhatsApp within minutes."
      />
      <div className="mt-10 flex flex-wrap gap-2">
        {FILTERS.map((f) => (
          <button
            key={f.id}
            data-testid={`fleet-filter-${f.id}`}
            onClick={() => setFilter(f.id)}
            className={`rounded-full border px-4 py-2 text-sm transition-colors ${
              filter === f.id
                ? "border-[#D4AF37] bg-[#D4AF37] font-semibold text-[#0F1418]"
                : "border-white/15 text-white/60 hover:border-[#D4AF37]/50 hover:text-white"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>
      <motion.div layout className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        <AnimatePresence mode="popLayout">
          {visible.map((v) => (
            <motion.article
              key={v.id}
              layout
              data-testid="fleet-card-item"
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.96 }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
              className="gold-glow-hover group overflow-hidden rounded-2xl border border-white/10 bg-[#171F26]"
            >
              <div className="relative h-44 overflow-hidden">
                <img src={v.image} alt={v.alt || v.name} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <span className="absolute right-4 top-4 rounded-full bg-[#D4AF37] px-3 py-1 text-xs font-bold text-[#0F1418]">{v.rate}</span>
              </div>
              <div className="flex items-center justify-between p-6">
                <div>
                  <h3 className="font-serif text-lg font-semibold">{v.name}</h3>
                  <p className="mt-1 flex items-center gap-1.5 text-sm text-white/50">
                    <Users className="h-3.5 w-3.5" /> {v.seats}
                  </p>
                </div>
                <a
                  href={waLink(`Hello Jay Jagannath Tour & Travels, I want to reserve the ${v.name}. Please share availability and quote.`)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="fleet-book-whatsapp"
                  aria-label={`Reserve ${v.name} on WhatsApp`}
                  className="flex h-10 w-10 items-center justify-center rounded-full border border-white/15 text-white/70 transition-colors hover:border-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#0F1418]"
                >
                  <ArrowUpRight className="h-4 w-4" />
                </a>
              </div>
            </motion.article>
          ))}
        </AnimatePresence>
      </motion.div>
      <p className="mt-6 text-xs text-white/40">Rates are indicative and may vary by route, season and duration. Final quote confirmed on WhatsApp.</p>
    </section>
  );
}
